import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import {
  ChevronDownIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

import { useAlert } from 'context/AlertContext';
import Faq from '../../../components/Faq';
import {
  sendHelpMessage,
  clearHelpStatus,
} from '../../../redux/actions/help';

/* ─────────────────────────── shared atoms (local) ────────────────────────── */

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.14em] text-muted font-bold mb-1.5">
    {children}
  </div>
);

const TabHeader = ({ eyebrow, title, sub }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-6 pb-5 border-b border-line">
    <div className="min-w-0">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="text-[26px] sm:text-[30px] md:text-[34px] leading-[1.05] tracking-tight text-ink-strong font-bold break-words">
        {title}
      </h2>
      {sub && (
        <p className="text-[14px] text-muted mt-2 max-w-xl leading-relaxed">
          {sub}
        </p>
      )}
    </div>
  </div>
);

const Card = ({ children, className = '', padded = true }) => (
  <section
    className={`bg-white border border-line rounded-2xl shadow-softer ${
      padded ? 'p-6' : ''
    } ${className}`}
  >
    {children}
  </section>
);

const CardHeader = ({ eyebrow, title, sub }) => (
  <header className="mb-5">
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h3 className="text-[17px] sm:text-[19px] font-bold tracking-tight text-ink-strong leading-snug">
      {title}
    </h3>
    {sub && <p className="text-sm text-muted mt-1 max-w-prose">{sub}</p>}
  </header>
);

const FieldLabel = ({ children }) => (
  <div className="text-[10.5px] uppercase tracking-[0.08em] text-yellow-03 font-bold mb-1.5">
    {children}
  </div>
);

/* ─────────────────────────────── Help ────────────────────────────────────── */

const TOPIC_KEYS = [
  'Account',
  'Billing',
  'Notifications',
  'Security',
  'Settings',
  'Subscription',
  'Support',
  'Team',
];

const MIN_MESSAGE_LENGTH = 5;
const MAX_MESSAGE_LENGTH = 6000;

const Help = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();

  const { loading, success, error } = useSelector((state) => state.help);

  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState('');

  const messageTrimmed = message.trim();
  const tooShort = messageTrimmed.length < MIN_MESSAGE_LENGTH;
  const canSend = !!topic && !tooShort && !loading;

  const onSend = useCallback(() => {
    if (!canSend) return;
    dispatch(sendHelpMessage({ topic, message: messageTrimmed }));
  }, [canSend, dispatch, topic, messageTrimmed]);

  useEffect(() => {
    if (!success) return;
    showAlert({
      type: 'success',
      title: t('dashboard.help_page.send_success_title'),
      message: t('dashboard.help_page.send_success_message'),
      primaryAction: { label: t('common.ok') },
    });
    setTopic('');
    setMessage('');
    dispatch(clearHelpStatus());
  }, [success, showAlert, dispatch, t]);

  useEffect(() => {
    if (!error) return;
    showAlert({
      title: t('dashboard.help_page.send_error_title'),
      message: error || t('dashboard.help_page.send_error_message'),
      primaryAction: { label: t('common.ok') },
    });
    dispatch(clearHelpStatus());
  }, [error, showAlert, dispatch, t]);

  useEffect(() => () => dispatch(clearHelpStatus()), [dispatch]);

  const hint = !topic
    ? t('dashboard.help_page.topic_required')
    : tooShort
      ? t('dashboard.help_page.message_min_hint')
      : '';

  return (
    <div className="space-y-6">
      <TabHeader
        eyebrow={t('dashboard.help_page.eyebrow')}
        title={t('dashboard.help_page.title')}
        sub={t('dashboard.help_page.subtitle')}
      />

      <Faq />

      <Card>
        <CardHeader
          eyebrow={t('dashboard.help_page.contact_eyebrow')}
          title={t('dashboard.help_page.contact_title')}
          sub={t('dashboard.help_page.subtitle')}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block md:col-span-1">
            <FieldLabel>{t('dashboard.help_page.topic_label')}</FieldLabel>
            <div className="relative mt-1.5">
              <select
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="w-full h-11 px-3.5 pr-9 rounded-xl bg-white border border-line text-[13.5px] text-ink-strong appearance-none focus:outline-none focus:border-yellow-03 transition-colors"
              >
                <option value="" disabled>
                  {t('dashboard.help_page.topic_placeholder')}
                </option>
                {TOPIC_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {t(`dashboard.help_page.topics.${k}`, k)}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
            </div>
          </label>

          <label className="block md:col-span-2">
            <FieldLabel>{t('dashboard.help_page.message_label')}</FieldLabel>
            <textarea
              value={message}
              onChange={(e) =>
                setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))
              }
              placeholder={t('dashboard.help_page.message_placeholder')}
              className="w-full min-h-[180px] px-3.5 py-3 rounded-xl bg-white border border-line text-[14px] text-ink placeholder:text-muted focus:outline-none focus:border-yellow-03 transition-colors resize-y"
            />
            <div className="mt-1.5 flex items-center justify-between text-[12px]">
              <span className="text-muted">{hint || ' '}</span>
              <span className="text-muted font-mono">
                {messageTrimmed.length}/{MAX_MESSAGE_LENGTH}
              </span>
            </div>
          </label>
        </div>

        <div className="mt-6 pt-5 border-t border-line flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onSend}
            disabled={!canSend}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-brand hover:bg-brand-hover text-ink-strong text-[13px] font-bold shadow-softer disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <ClipLoader color="#1A1A1F" size={14} />
            ) : (
              <PaperAirplaneIcon className="w-4 h-4" />
            )}
            {loading
              ? t('dashboard.help_page.sending')
              : t('dashboard.help_page.send')}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Help;
